import { LocationQueryValue } from 'vue-router'
import { StateField, StateFieldConfig } from '../StateField'
import { GalleryInput } from '@base/lib/imageUploads/GalleryInput'
import { ref, Ref } from 'vue'
import { useClient } from '@base/api/useClient'
import { EntityWithUpload } from '@base/lib/imageUploads/EntityWithUpload'
import { handleGalleryUpload } from '@base/lib/imageUploads/handleGalleryUpload'

export type GalleryStateFieldConfig = StateFieldConfig<GalleryStateField> & {
  type: 'gallery'

  associatedEntity: EntityWithUpload
  default?: GalleryInput[]
  overrideValue?: Ref<GalleryInput[]>

  maxSize?: number // Max image size in bytes
  maxNumber?: number // Max number of images
}

export class GalleryStateField extends StateField {
  readonly type = 'gallery'
  default: GalleryInput[] = []
  value: Ref<GalleryInput[]>
  private associatedEntity: EntityWithUpload
  private client = useClient()

  maxSize?: number
  maxNumber?: number

  constructor(config: GalleryStateFieldConfig) {
    super(config)

    this.associatedEntity = config.associatedEntity
    this.value = config.overrideValue || ref(config.default || [])
    this.maxSize = config.maxSize
    this.maxNumber = config.maxNumber
  }

  toRouteValue(): string | undefined {
    if (this.toRouteValueOverride) return this.toRouteValueOverride(this)

    return undefined
  }

  pullRouteQueryValue(value: LocationQueryValue | LocationQueryValue[]): void {
    if (this.pullRouteQueryValueOverride)
      return this.pullRouteQueryValueOverride(this, value)

    return
  }

  pullRouteParamValue(value: string | string[]): void {
    if (this.pullRouteParamValueOverride)
      return this.pullRouteParamValueOverride(this, value)

    return
  }

  async toRequestVariable(variation: number) {
    if (this.toRequestVariableOverride)
      return await this.toRequestVariableOverride(this, variation)

    let newGallery: string[] | undefined

    if (this.updated.value) {
      const [updatePaths, err] = await handleGalleryUpload(
        this.value.value,
        this.client,
        this.associatedEntity
      )
      if (err) {
        console.error(err)
        throw new Error('Error uploading gallery')
      }
      newGallery = updatePaths
    }

    return newGallery
  }

  resetValue(): void {
    if (this.resetValueOverride) return this.resetValueOverride(this)

    this.value.value = this.default || []
  }
}
